import { CSSProperties, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { procedureStatusColorsQuery, ProcedureStatusColor } from '@/graph/procedureStatusColors';
import { getLegacyAnesthesiologistColor, getLegacyPanelColor, getLegacyScheduleColor } from '@/util/legacyStatusColors';
import { normalizeStatus, NormalizedStatus } from '@/components/pages/kiosk/schedule/shared/procedureUtils';
import './statusContour.css';

export type StatusColorVariant = 'schedule' | 'anesthesiologist' | 'panel';
type StatusColorMap = Record<string, ProcedureStatusColor>;
const noExclusions: ReadonlySet<string> = new Set();
const flashStyle = (color: string) =>
  ({
    animation: 'procedureStatusContourFlash 1.2s ease-in alternate infinite',
    '--status-contour-color': color,
  }) as CSSProperties;
type StatusRoom = { type?: string | null } | null;

// Flags use schedule step names; canonical patient-status names resolve through the existing normalizer.
// Ready is already the schedule name for ReadyForSurgery; a patient's raw Ready status normalizes to OR.
const configFlagStatus = (status: string) => {
  if (status === NormalizedStatus.Ready) return status;
  const normalized = normalizeStatus(status);
  return normalized === NormalizedStatus.Unknown ? status : normalized;
};

/** Existing callers that only need a color keep the same interface. */
export function useStatusColor(variant: StatusColorVariant) {
  return useStatusDisplay(variant).getColor;
}

/** Exclusions disable status coloring and flashing on one screen; they never filter procedures. */
export function useStatusDisplay(variant: StatusColorVariant = 'schedule', options?: { skip?: boolean }) {
  const { data } = useQuery<{ procedureStatusColors: ProcedureStatusColor[] }>(procedureStatusColorsQuery, options);
  const { backendMap, flashingColors, excludedFromSchedule, excludedFromMonitor } = useMemo(() => {
    const rows = data?.procedureStatusColors;
    if (!Array.isArray(rows) || rows.length === 0) {
      return {
        backendMap: null,
        flashingColors: {},
        excludedFromSchedule: noExclusions,
        excludedFromMonitor: noExclusions,
      };
    }
    const backendMap: StatusColorMap = {};
    const flashingColors: Record<string, string> = {};
    const excludedFromSchedule = new Set<string>();
    const excludedFromMonitor = new Set<string>();
    for (const row of rows) {
      if (row?.status && row?.color) backendMap[row.status] = row;
      if (!row?.status) continue;
      const step = configFlagStatus(row.status);
      if (row.flash && (row.status === step || !flashingColors[step])) flashingColors[step] = row.color;
      if (row.excludeFromSchedule) excludedFromSchedule.add(step);
      if (row.excludeFromMonitor) excludedFromMonitor.add(step);
    }
    return { backendMap, flashingColors, excludedFromSchedule, excludedFromMonitor };
  }, [data?.procedureStatusColors]);

  const excludedStatuses = variant === 'panel' ? excludedFromMonitor : excludedFromSchedule;
  const exclusionField = variant === 'panel' ? 'excludeFromMonitor' : 'excludeFromSchedule';
  const isColorExcluded = useCallback(
    (status: string, room?: StatusRoom, isDpuAsPreOp = false) => {
      const normalized = variant === 'panel' ? normalizeStatus(status, room, isDpuAsPreOp) : status;
      return Boolean(backendMap?.[status]?.[exclusionField]) || excludedStatuses.has(normalized);
    },
    [backendMap, excludedStatuses, exclusionField, variant]
  );

  const getColor = useCallback(
    (status: string, hospitalId?: number, defaultColor?: string | null, room?: StatusRoom, isDpuAsPreOp = false) => {
      // An exclusion drops only the configured status color; callers passing null (borders) still get none.
      if (backendMap) {
        if (backendMap[status] != null && !isColorExcluded(status, room, isDpuAsPreOp)) return backendMap[status].color;
        if (variant === 'panel') return defaultColor === undefined ? null : defaultColor;
        return defaultColor === undefined ? '#00A7F7' : defaultColor;
      }
      switch (variant) {
        case 'anesthesiologist':
          return getLegacyAnesthesiologistColor(status, hospitalId, defaultColor);
        case 'panel':
          return getLegacyPanelColor(status, hospitalId);
        case 'schedule':
        default:
          return getLegacyScheduleColor(status, hospitalId, defaultColor);
      }
    },
    [backendMap, isColorExcluded, variant]
  );
  const getFlashingContourColor = useCallback(
    (status?: string | null, room?: StatusRoom, isDpuAsPreOp = false) => {
      if (!status || isColorExcluded(status, room, isDpuAsPreOp)) return undefined;
      const normalized = variant === 'panel' ? normalizeStatus(status, room, isDpuAsPreOp) : status;
      return flashingColors[normalized === NormalizedStatus.Unknown ? status : normalized];
    },
    [flashingColors, isColorExcluded, variant]
  );
  /** `color` is the contour the caller already draws, so the flash peaks at the same color. */
  const getContourStyle = useCallback(
    (status?: string | null, color?: string | null, room?: StatusRoom, isDpuAsPreOp = false) => {
      const flashColor = getFlashingContourColor(status, room, isDpuAsPreOp);
      return flashColor ? flashStyle(color || flashColor) : undefined;
    },
    [getFlashingContourColor]
  );
  return { getColor, getContourStyle, getFlashingContourColor };
}
